import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getContactData } from '../../../Public/contact-data.js';
import * as authActions from '../../../Redux/Auth/AuthActions.js';
import ContactService from '../../../Services/ContactService.js';

// Custom Components
import Selector from '../../Select/select-component.js';
import { ButtonBlack, ButtonPrimary, ButtonSecondary } from '../../Buttons/button-component.js';
import { InputComponent, TextAreaComponent } from '../../Inputs/input-component.js';

// --- Helpers ---
const isOtherValue = (val) => {
    if (!val) return false;
    const s = String(val).trim().toLowerCase();
    return s === 'other' || s === 'אחר';
};

const ContactForm = ({ onSuccess }) => {
    const dispatch = useDispatch();

    // 1. Global State
    const { isAuthenticated } = useSelector((state) => state.auth);
    const { language, dir } = useSelector((state) => state.systemData || {});

    // 2. Data Source
    const data = getContactData(language);
    const txt = data.mainForm;
    const topics = data.topics;
    const consentTxt = data.consent;

    // 3. Local State
    const [formData, setFormData] = useState({
        email: '',
        fullName: '',
        phone: '',
        subject: '',
        customSubject: ''
    });
    const [status, setStatus] = useState('idle');
    const [marketingConsent, setMarketingConsent] = useState(true);
    const [termsConsent,     setTermsConsent]     = useState(false);

    useEffect(() => {
        dispatch(authActions.isLoggedIn());
    }, [dispatch]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.email && !formData.phone) return;

        setStatus('loading');
        const { email, fullName, phone, subject, customSubject } = formData;
        const preferredContact = { marketing: marketingConsent, terms: termsConsent };

        const otherSelected = isOtherValue(subject);
        const finalSubject = otherSelected ? (customSubject || 'General Inquiry') : (subject || 'General Inquiry');
        const leadSource = txt.leadSource;

        try {
            // Contact (under the account) + email are both handled by sendContact → /public/contact
            await ContactService.sendContact({
                fullName: String(fullName || '').trim(),
                email,
                phone,
                subject: finalSubject,
                message: customSubject,
                leadSource,
                preferredContact
            });

            setStatus('success');
            if (onSuccess) onSuccess();
            setFormData({ email: '', fullName: '', phone: '', subject: '', customSubject: '' });
            setTimeout(() => setStatus('idle'), 3000);
        } catch (error) {
            console.error('Form submission error:', error);
            setStatus('idle');
        }
    };

    return (
        <div dir={dir} className="flex w-full items-center justify-center lg:justify-start relative">
            <form className="w-full flex flex-col gap-6" onSubmit={handleSubmit}>

                {/* Header */}
                <div className="flex flex-col gap-2 text-left">
                    <h2 className="text-balance text-2xl lg:text-3xl font-black text-primary-950">
                        {txt.title}
                    </h2>
                    <div className="w-20 h-0.5 bg-secondary-500" />
                    <p className="text-pretty text-sm text-primary-900/60 max-w-xl leading-relaxed">
                        {txt.subtitle}
                    </p>
                </div>

                {/* Industrial Input Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 w-full">

                    {/* Name */}
                    <InputComponent
                        size="lg"
                        className="!bg-white/5 !border-white/10 !rounded-none focus:!border-secondary-500"
                        placeholder={txt.placeholders.name}
                        value={formData.fullName}
                        onChange={(e) => handleChange('fullName', e.target.value)}
                        required
                    />

                    {/* Phone */}
                    <InputComponent
                        size="lg"
                        type="tel"
                        className="!bg-white/5 !border-white/10 !rounded-none focus:!border-secondary-500"
                        placeholder={txt.placeholders.phone}
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                    />

                    {/* Email */}
                    <div className="md:col-span-2">
                        <InputComponent
                            size="lg"
                            type="email"
                            dir="ltr"
                            className={`!bg-white/5 !border-white/10 !rounded-none focus:!border-secondary-500 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}
                            placeholder={txt.placeholders.email}
                            value={formData.email}
                            onChange={(e) => handleChange('email', e.target.value)}
                        />
                    </div>

                    {/* Service Selector */}
                    <div className="md:col-span-2">
                        <Selector
                            options={topics}
                            value={formData.subject}
                            placeholder={txt.placeholders.selectService}
                            onChange={(val) => handleChange('subject', val)}
                            size="lg"
                            className="w-full"
                            triggerClassName="!rounded-none !bg-white/5 !border-white/10 focus:!border-secondary-500"
                        />
                    </div>

                    {/* Message Area */}
                    {(isOtherValue(formData.subject) || isAuthenticated || formData.subject) && (
                        <div className="md:col-span-2 relative z-0">
                            <TextAreaComponent
                                size="lg"
                                rows={3}
                                className="!bg-white/5 !border-white/10 !rounded-none focus:!border-secondary-500"
                                placeholder={txt.placeholders.message}
                                value={formData.customSubject}
                                onChange={(e) => handleChange('customSubject', e.target.value)}
                            />
                        </div>
                    )}

                </div>

                {/* Consent checkboxes */}
                <div className="space-y-2 pt-1">
                    <label className="flex items-start  text-left gap-2.5 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={marketingConsent}
                            onChange={e => setMarketingConsent(e.target.checked)}
                            className="mt-0.5 accent-secondary-500 w-4 h-4 shrink-0"
                        />
                        <span className="text-xs text-gray-500 font-medium leading-relaxed">{consentTxt.marketing}</span>
                    </label>
                    <label className="flex items-start text-left gap-2.5 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={termsConsent}
                            onChange={e => setTermsConsent(e.target.checked)}
                            className="mt-0.5 accent-secondary-500 w-4 h-4 shrink-0"
                        />
                        <span className="text-xs text-gray-500 font-medium leading-relaxed">
                            {consentTxt.terms}{' '}
                            <a href={consentTxt.termsLink} target="_blank" rel="noopener noreferrer" className="underline text-gray-400 hover:text-white transition-colors">
                                Terms of Service
                            </a>
                        </span>
                    </label>
                </div>

                {/* CTA */}
                <div className="w-full md:max-w-md self-center lg:self-start mt-2 relative z-0">
                    <ButtonSecondary
                        type="submit"
                        fullWidth
                        size="lg"
                        className="!rounded-none uppercase font-black"
                        disabled={status === 'loading'}
                    >
                        <span>{status === 'loading' ? txt.sendingLabel : txt.submitLabel}</span>
                    </ButtonSecondary>
                </div>

            </form>
        </div>
    );
};

export default ContactForm;